// Globe picking for the HUD (docs/ARCHITECTURE.md §8, GDD §14): the city under a screen point (nearest city node on
// the visible hemisphere within a pick radius). Called from input events only, never per frame.

import { Vector3 } from 'three'
import type { CityIndex, MatchSetupView } from '@taxwar/net'
import type { GlobePicker } from '../../match/contracts'
import type { GlobeCameraRig } from '../../render/camera/GlobeCameraRig'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../../render/geo/globeSpace'

/** How far (CSS px) from a city node a click still picks it. */
const CITY_PICK_RADIUS_PX = 18
const NDC_SPAN = 2

export class WarGlobePicker implements GlobePicker {
  readonly surface: Element
  private readonly cities: readonly Vector3[]
  private readonly projected = new Vector3()
  private readonly toCamera = new Vector3()

  constructor(
    private readonly rig: GlobeCameraRig,
    private readonly canvas: HTMLCanvasElement,
    setup: MatchSetupView,
  ) {
    this.surface = canvas
    this.cities = setup.cities.map((city) => latLonToVector3(city.lat, city.lon, GLOBE_ALTITUDE.network, new Vector3()))
  }

  cityAt(clientX: number, clientY: number): CityIndex | null {
    const camera = this.rig.camera
    camera.updateMatrixWorld()
    const rect = this.canvas.getBoundingClientRect()
    let best: CityIndex | null = null
    let bestDistanceSq = CITY_PICK_RADIUS_PX * CITY_PICK_RADIUS_PX
    for (let i = 0; i < this.cities.length; i++) {
      const city = this.cities[i] as Vector3
      // Cities on the far hemisphere face away from the camera.
      if (this.toCamera.copy(camera.position).sub(city).dot(city) <= 0) continue
      this.projected.copy(city).project(camera)
      const x = rect.left + ((this.projected.x + 1) / NDC_SPAN) * rect.width
      const y = rect.top + ((1 - this.projected.y) / NDC_SPAN) * rect.height
      const distanceSq = (x - clientX) * (x - clientX) + (y - clientY) * (y - clientY)
      if (distanceSq < bestDistanceSq) {
        bestDistanceSq = distanceSq
        best = i as CityIndex
      }
    }
    return best
  }

  focus(latDeg: number, lonDeg: number): void {
    this.rig.focus(latDeg, lonDeg)
  }
}
